<script setup lang="ts">
import { reactive } from 'vue';
import { defineRule, Form, Field } from 'vee-validate';
import { max } from '@vee-validate/rules';

import type { PublishWithNoteModalData } from '../../../../App/Publish/Scripts/publish-index-model';
import { appStudioModalService } from '../Modal/app-studio-modal';
import StModal from '../Modal/StModal.vue';

defineOptions({ name: 'app-publish-modal-component' });
defineProps<{
    maximumSnapshotsCountExceeded: boolean;
    appHasExpressionErrors: boolean;
    expressionErrorsUrl: string;
    noteSupportText: string;
    snapshotSupportText: string;
}>();
const emit = defineEmits<{ (event: 'on-publish-version-submit', value: PublishWithNoteModalData): void }>();
defineRule('max', max);
const publishWithNoteModalData = reactive<PublishWithNoteModalData>({ notes: '', takeASnapshot: false });
function onHidePublishWithNoteModal(): void {
    appStudioModalService.hide('publishWithNoteModal');
}
function onSubmitPublishWithNoteModal(): void {
    emit('on-publish-version-submit', publishWithNoteModalData);
    onHidePublishWithNoteModal();
}
function focusElement(elementId: string): void {
    document.getElementById(elementId)?.focus();
}
</script>

<template>
    <StModal id="publishWithNoteModal" ref="publishWithNoteModal" size="lg" :hide-footer="true" @shown="focusElement('note')">
        <template #title>Publish Version</template>
        <Form as="form" @submit="onSubmitPublishWithNoteModal">
            <div v-if="appHasExpressionErrors" class="alert alert-danger appstudio-flush-alert" role="alert">
                <a :href="expressionErrorsUrl" class="alert-anchor-link">
                    <b>
                        <i class="fal fa-exclamation-triangle"></i>
                        Warning:
                    </b>
                    Field Expression Errors are present
                </a>
            </div>
            <Field name="Notes" :rules="{ max: 500 }" #default="{ errors }" :model-value="publishWithNoteModalData.notes">
                <div class="form-group appstudio-form-group-section-start">
                    <label for="note">Notes</label>
                    <textarea
                        id="note"
                        v-model="publishWithNoteModalData.notes"
                        name="note"
                        rows="3"
                        :class="{ 'form-control': true, 'is-invalid': errors.length > 0 }"
                    ></textarea>
                    <div v-show="errors.length > 0" class="invalid-feedback">{{ errors[0] }}</div>
                </div>
                <div class="detailsQuote">
                    <small>{{ noteSupportText }}</small>
                </div>
            </Field>
            <div class="appstudio-compact-end">
                <div class="form-check form-switch">
                    <input
                        id="takeASnapshot"
                        v-model="publishWithNoteModalData.takeASnapshot"
                        name="takeASnapshot"
                        type="checkbox"
                        class="form-check-input"
                        :disabled="maximumSnapshotsCountExceeded"
                    />
                    <label for="takeASnapshot" class="form-check-label">Take a Snapshot?</label>
                </div>
                <div class="detailsQuote">
                    <small>{{ snapshotSupportText }}</small>
                </div>
            </div>
            <div class="form-actions">
                <button type="submit" class="btn btn-primary">Ok</button>
                <a href="#" @click.prevent="onHidePublishWithNoteModal">Cancel</a>
            </div>
        </Form>
    </StModal>
</template>
